import * as Yup from 'yup'
import _, { isNil } from 'lodash'
import { initialSpaceRegex, offerNameInvalidErrMsg } from './constants'
import { isEmpty } from 'lodash'
import { DateTime } from 'luxon'
import { mandatoryFieldMessage } from './messagesContants'
import { Installments } from '../../types/flyout'
import './globalValidations'
declare module 'yup' {
  interface ArraySchema<T> {
    uniqueProperty(
      propertyPath: string,
      message?: string,
      typesExcludedFromUniqueness?: string[]
    ): ArraySchema<T>
    uniquePropertyCustomType(
      propertyPath: string,
      message?: string,
      dimensions?: any
    ): ArraySchema<T>
    installmentsValidation(
      propertyPath: string,
      timeZone?: string,
      message?: string
    ): ArraySchema<T>
  }
}
Yup.addMethod(
  Yup.array,
  'uniquePropertyCustomType',
  function (propertyPath, message, dimensions = []) {
    return this.test('unique', '', function (list: any) {
      const errors: any[] = []

      list?.forEach((item: any, index: number) => {
        const propertyValue = _.get(item, propertyPath)
        const normalizedValue =
          typeof propertyValue === 'string'
            ? propertyValue.toLowerCase()
            : propertyValue
        const type = item?.type

        if (
          type === 'custom' &&
          propertyValue &&
          _.filter(list, (obj: any) => {
            const otherValue = _.get(obj, propertyPath)
            const normalizedOtherValue =
              typeof otherValue === 'string'
                ? otherValue.toLowerCase()
                : otherValue
            return (
              obj?.type === 'custom' && normalizedOtherValue === normalizedValue
            )
          }).length > 1
        ) {
          errors.push(
            this.createError({
              path: `${this.path}[${index}].${propertyPath}`,
              message,
            })
          )
        }

        if (
          type === 'custom' &&
          propertyValue &&
          dimensions
            .map((v: string) => v.toLowerCase())
            .includes(normalizedValue)
        ) {
          errors.push(
            this.createError({
              path: `${this.path}[${index}].${propertyPath}`,
              message,
            })
          )
        }
      })

      if (!_.isEmpty(errors)) {
        throw new Yup.ValidationError(errors)
      }

      return true
    })
  }
)

/**
 * Validates that the selected property has unique values across the array,
 * excluding configured item types and allowed additional usage items.
 */
const uniquePropertyMethod = function (
  this: Yup.ArraySchema<Yup.AnySchema>,
  propertyPath: string,
  message?: string,
  typesExcludedFromUniqueness: string[] = []
) {
  return this.test('unique', '', function (list: any) {
    const errors: any = []
    const isCounted = (item: any) => {
      if (typesExcludedFromUniqueness.includes(item?.type)) {
        return false
      }
      return !(
        typesExcludedFromUniqueness.includes('additional_usage') &&
        item?.isAdditionalUsageAllowed
      )
    }

    list?.forEach((item: any, index: number) => {
      const propertyValue = _.get(item, propertyPath)
      if (!isCounted(item) || !propertyValue) {
        return
      }
      if (
        _.filter(
          list,
          (other: any) =>
            isCounted(other) && _.get(other, propertyPath) === propertyValue
        ).length > 1
      ) {
        errors.push(
          this.createError({
            path: `${this.path}[${index}].${propertyPath}`,
            message,
          })
        )
      }
    })

    if (!_.isEmpty(errors)) {
      throw new Yup.ValidationError(errors)
    }

    return true
  })
}
Yup.addMethod(Yup.array, 'uniqueProperty', uniquePropertyMethod)

Yup.addMethod(
  Yup.array,
  'installmentsValidation',
  function (propertyPath, timeZone, message) {
    return this.test('installmentsValidation', '', function (list: any) {
      const errors: any = []
      const offerExpirationDate = this.options?.context?.offerExpirationDate
      const paymentDatesBeforeExpirationDate = _.filter(
        list,
        (item: Installments) =>
          DateTime.fromISO(item.paymentDate, { zone: timeZone }) <=
          DateTime.fromISO(offerExpirationDate, { zone: timeZone })
      ).map(item => item.paymentDate)

      list?.forEach((item: any, index: number) => {
        if (
          paymentDatesBeforeExpirationDate.length >= 2 &&
          paymentDatesBeforeExpirationDate.includes(item.paymentDate)
        ) {
          errors.push(
            this.createError({
              path: `${this.path}[${index}].${propertyPath}`,
              message,
            })
          )
        }
      })

      if (!_.isEmpty(errors)) {
        throw new Yup.ValidationError(errors)
      }

      return true
    })
  }
)
const agreementBasedOfferAddEditValidationSchema = (
  timeZone: string,
  dimensions?: any
) => {
  Yup.addMethod(Yup.array, 'uniqueProperty', uniquePropertyMethod)
  return Yup.object().shape({
    //add validation here
    companyName: Yup.string().required('Company name cannot be empty.'),
    privateOfferName: Yup.string()
      .required('Offer name cannot be empty.')
      .test(
        'contains-specific-characters',
        `${offerNameInvalidErrMsg}`,
        (value: string | undefined) => {
          if (!value) {
            return true
          }
          return !(
            value.includes('\\') ||
            value.includes('>') ||
            value.includes('<') ||
            !new RegExp(initialSpaceRegex).test(value)
          )
        }
      ),
    renewal: Yup.mixed().test(
      'is-renewal-required',
      'Renewal cannot be empty.',
      function (value: string | boolean) {
        if (!value && value !== false) {
          return false
        }
        return true
      }
    ),
    offerExpirationDate: Yup.string()
      .nullable()
      .required('Date field cannot be empty.')
      .test(
        'check offer expiration date with previous subscription end date',
        'offer expiration date must be smaller than the previous service end date.',
        function (value?: string | null) {
          if (
            !isEmpty(value) &&
            !isEmpty(this.parent.previousSubscriptionEndDate)
          ) {
            return (
              DateTime.fromISO(this.parent.previousSubscriptionEndDate, {
                zone: timeZone,
              }) > DateTime.fromISO(value || '', { zone: timeZone })
            )
          }
          return true
        }
      )
      .test(
        'check offer expiration date',
        'offer expiration date must be smaller than the service end date.',
        function (value?: string | null) {
          if (!isEmpty(value) && !isEmpty(this.parent.subscriptionEndDate)) {
            return (
              DateTime.fromISO(this.parent.subscriptionEndDate, {
                zone: timeZone,
              }) >= DateTime.fromISO(value || '', { zone: timeZone })
            )
          }
          return true
        }
      ),
    emailCustomMessage: Yup.string().required(
      'Please enter the Custom email message.'
    ),
    flexiblePaymentTerms: Yup.boolean(),
    dimensions: Yup.array().when('flexiblePaymentTerms', {
      is: (flexiblePaymentTerms: boolean) => flexiblePaymentTerms,
      then: Yup.array()
        .of(
          Yup.object().shape({
            type: Yup.string(),
            name: Yup.string().when('type', {
              is: (type: string) => type === 'custom',
              then: Yup.string(),
              otherwise: Yup.string(),
            }),
            label: Yup.string().when('type', {
              is: (type: string) => type === 'custom',
              then: Yup.string()
                .trim(mandatoryFieldMessage)
                .strict(true)
                .max(24, 'Max 24 characters are allowed')
                .required(mandatoryFieldMessage),
              otherwise: Yup.string(),
            }),
            labelDescription: Yup.string().when('type', {
              is: (type: string) => type === 'custom',
              then: Yup.string()
                .trim(mandatoryFieldMessage)
                .strict(true)
                .max(1000, 'Max 1000 characters are allowed')
                .required(mandatoryFieldMessage),
              otherwise: Yup.string(),
            }),
            quantity: Yup.number().when('type', {
              is: (type: string) => type === 'custom' || type === 'standard',
              then: Yup.number()
                .typeError('Please enter a number')
                .required('Please enter quantity')
                .positive('quantity cannot take negative values')
                .integer('quantity cannot take decimal values')
                .min(1, 'quantity should be more than 0'),
              otherwise: Yup.number().nullable(),
            }),
            isAdditionalUsageAllowed: Yup.boolean(),
            price: Yup.number().when(['type', 'isAdditionalUsageAllowed'], {
              is: (type: string, isAdditionalUsageAllowed?: boolean) =>
                type === 'additional_usage' || isAdditionalUsageAllowed,
              then: Yup.number()
                .typeError('Please enter a number')
                .required('Please enter price')
                .test(
                  'greater-than-or-zero',
                  'value must be greater than or equal to 0',
                  function (value?: number | null) {
                    return !isNil(value) && value >= 0
                  }
                )
                .checkThreeDecimalpointValidation({
                  message: 'Only 3 decimal places allowed.',
                }),
              otherwise: Yup.number().nullable(),
            }),
          })
        )
        .uniqueProperty('name', 'API identifier must be unique', [
          'additional_usage',
        ])
        .uniquePropertyCustomType(
          'label',
          'Display name must be unique',
          dimensions
        ),
      otherwise: Yup.array()
        .of(
          Yup.object().shape({
            type: Yup.string(),
            name: Yup.string().when('type', {
              is: (type: string) => type === 'custom',
              then: Yup.string(),
              otherwise: Yup.string(),
            }),
            label: Yup.string().when('type', {
              is: (type: string) => type === 'custom',
              then: Yup.string()
                .trim(mandatoryFieldMessage)
                .strict(true)
                .max(24, 'Max 24 characters are allowed')
                .required(mandatoryFieldMessage),
              otherwise: Yup.string(),
            }),
            labelDescription: Yup.string().when('type', {
              is: (type: string) => type === 'custom',
              then: Yup.string()
                .trim(mandatoryFieldMessage)
                .strict(true)
                .max(1000, 'Max 1000 characters are allowed')
                .required(mandatoryFieldMessage),
              otherwise: Yup.string(),
            }),
            isAdditionalUsageAllowed: Yup.boolean(),
            price: Yup.number().when(['type', 'isAdditionalUsageAllowed'], {
              is: (type: string, isAdditionalUsageAllowed?: boolean) =>
                type === 'custom' ||
                type === 'additional_usage' ||
                type === 'standard' ||
                isAdditionalUsageAllowed,
              then: Yup.number()
                .typeError('Please enter a number')
                .required('Please enter price')
                .test(
                  'greater-than-or-equal-zero',
                  'value must be greater than or equal to 0',
                  function (value?: number | null) {
                    return !isNil(value) && value >= 0
                  }
                )
                .checkThreeDecimalpointValidation({
                  message: 'Only 3 decimal places allowed.',
                }),
              otherwise: Yup.number().nullable(),
            }),
          })
        )
        .uniqueProperty('name', 'API identifier must be unique', [
          'additional_usage',
        ])
        .uniquePropertyCustomType(
          'label',
          'Display name must be unique',
          dimensions
        ),
    }),
    subscriptionEndDate: Yup.string()
      .nullable()
      .required('Date field cannot be empty.')
      .test(
        'check service end date',
        'Service end date must be greater than the offer expiration date.',
        function (value?: string | null) {
          if (!isEmpty(value) && !isEmpty(this.parent.offerExpirationDate)) {
            return (
              DateTime.fromISO(this.parent.offerExpirationDate, {
                zone: timeZone,
              }) <= DateTime.fromISO(value || '', { zone: timeZone })
            )
          }
          return true
        }
      ),
    offerRecipients: Yup.array()
      .of(
        Yup.object().shape({
          firstName: Yup.string().required('First Name cannot be empty.'),
          lastName: Yup.string().required('Last Name cannot be empty.'),
          title: Yup.string().nullable(),
          email: Yup.string()
            .email('Enter valid email.')
            .required('Email cannot be empty.'),
        })
      )
      .uniqueProperty('email', 'Duplicate emails are not allowed.'),
    installments: Yup.array()
      .of(
        Yup.object().shape({
          paymentDate: Yup.string()
            .nullable()
            .required('Payment date is required')
            .test(
              'check offer subscription end date',
              'The payment date cannot be after the service end date.',
              function (value?: string | null) {
                const subscriptionEndDate =
                  this.options?.context?.subscriptionEndDate
                if (!isEmpty(value) && !isEmpty(subscriptionEndDate)) {
                  return (
                    DateTime.fromISO(subscriptionEndDate, { zone: timeZone }) >=
                    DateTime.fromISO(value || '', { zone: timeZone })
                  )
                }
                return true
              }
            ),
          amount: Yup.number()
            .typeError('Please enter a number')
            .required('Please enter amount')
            .test(
              'check amount validation',
              'amount must be greater than or equal to 0',
              function (value?: number) {
                return !isNil(value) && value >= 0
              }
            )
            .checkDecimalpointValidation({
              message: 'Only 2 decimal places allowed.',
            }),
        })
      )
      .uniqueProperty('paymentDate', 'Duplicate payment dates are not allowed.')
      .installmentsValidation(
        'paymentDate',
        timeZone,
        'Only one payment date is allowed before offer expiration date.'
      ),
  })
}
export default agreementBasedOfferAddEditValidationSchema
